/**
 * Lazy Video
 *
 * Defers a background video's download until it is about to enter the viewport,
 * and pauses it once it leaves. Wraps a `<video>` that Liquid renders **without
 * a src** — the URL travels on `data-src` and is only assigned when the element
 * intersects, so the file is genuinely not requested on page load rather than
 * relying on `preload="none"` being honoured.
 *
 * Usage:
 *   <lazy-video data-src="https://cdn.shopify.com/videos/....mp4">
 *     <video class="…" loop muted playsinline preload="none" poster="…"></video>
 *   </lazy-video>
 *
 * Progressive enhancement: the `<video>` and its poster are server-rendered, so
 * with JavaScript broken the shopper sees the poster rather than a blank box.
 *
 * Deliberately a plain custom element rather than Lit. It owns no state and
 * renders no template — it only attaches behaviour to a child that Liquid
 * already rendered. A light-DOM Lit component would clear that child on its
 * first render pass.
 */

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/** Start fetching slightly before the section scrolls in, so playback looks instant. */
const DEFAULT_ROOT_MARGIN = "200px 0px";

export class LazyVideo extends HTMLElement {
  private _observer: IntersectionObserver | null = null;
  private _video: HTMLVideoElement | null = null;
  private _loaded = false;

  connectedCallback(): void {
    this._video = this.querySelector("video");
    if (!this._video || !this.dataset.src) return;

    // Reduced motion: never load the file at all. The poster is already
    // painted, so the section still looks complete.
    if (window.matchMedia?.(REDUCED_MOTION).matches) return;

    if (!("IntersectionObserver" in window)) {
      // No observer support — load immediately rather than never showing it.
      this._load();
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this._load();
            void this._video?.play().catch(() => {
              /* Autoplay can still be refused; the poster remains. */
            });
          } else if (this._loaded) {
            // Stop decode work while off-screen.
            this._video?.pause();
          }
        }
      },
      { rootMargin: this.dataset.rootMargin || DEFAULT_ROOT_MARGIN },
    );

    this._observer.observe(this);
  }

  disconnectedCallback(): void {
    this._observer?.disconnect();
    this._observer = null;
  }

  private _load(): void {
    if (this._loaded || !this._video || !this.dataset.src) return;
    this._loaded = true;
    this._video.src = this.dataset.src;
    this._video.load();
  }
}

if (!customElements.get("lazy-video")) {
  customElements.define("lazy-video", LazyVideo);
}
